/**
 * Voice pickers for both engines (§6.3).
 *
 * Cascaded: free-text voice ID input + a curated starter list per provider,
 * each row with a preview button stub (disabled — inline playback needs a
 * provider key in the browser, which v1 does not ship; the tooltip says so).
 *
 * Realtime: the fixed OpenAI voice set (REALTIME_VOICES) as selectable chips,
 * with the no-cloning note.
 *
 * Gemini: the fixed Google Live voice set (GEMINI_VOICES), same treatment.
 */

import { Field } from "../Field";
import { Input } from "../Input";
import { cx } from "../../lib/format";
import type { TTSProvider } from "../../lib/types";
import { GEMINI_VOICES, REALTIME_VOICES } from "../../lib/types";
import { STARTER_VOICES } from "./constants";
import { Tooltip } from "./Tooltip";

const PROVIDER_LABEL: Record<TTSProvider, string> = {
  cartesia: "Cartesia",
  deepgram: "Deepgram",
};

function PreviewButton({ provider, disabled }: { provider: TTSProvider; disabled: boolean }) {
  return (
    <Tooltip content={`Preview needs a ${PROVIDER_LABEL[provider]} key — test the voice in the browser test console instead.`}>
      <button
        type="button"
        disabled
        aria-label="Preview voice (unavailable)"
        className={cx(
          "flex h-7 w-7 items-center justify-center rounded border border-line text-muted",
          "cursor-not-allowed opacity-50",
          disabled && "pointer-events-none",
        )}
      >
        <svg viewBox="0 0 16 16" className="h-3 w-3" fill="currentColor" aria-hidden="true">
          <path d="M5 3.5v9l7-4.5-7-4.5Z" />
        </svg>
      </button>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// Cascaded picker
// ---------------------------------------------------------------------------

export interface CascadedVoicePickerProps {
  provider: TTSProvider;
  voiceId: string | null;
  onChange: (voiceId: string) => void;
  error?: string | null;
  disabled?: boolean;
}

export function CascadedVoicePicker({
  provider,
  voiceId,
  onChange,
  error,
  disabled = false,
}: CascadedVoicePickerProps) {
  const starters = STARTER_VOICES[provider];

  return (
    <div className="flex flex-col gap-3">
      <Field
        label="Voice ID"
        required
        error={error ?? undefined}
        help={`Any voice from the ${PROVIDER_LABEL[provider]} catalogue works — pick a starter below or paste an ID.`}
      >
        <Input
          mono
          value={voiceId ?? ""}
          disabled={disabled}
          placeholder={provider === "cartesia" ? "e.g. 694f9389-aac1-…" : "e.g. aura-2-thalia-en"}
          onChange={(event) => onChange(event.target.value)}
        />
      </Field>

      <ul className="flex flex-col divide-y divide-line rounded border border-line" role="listbox" aria-label="Starter voices">
        {starters.map((voice) => {
          const selected = voice.id === voiceId;
          return (
            <li key={voice.id} className="flex items-center gap-3 px-3 py-2">
              <button
                type="button"
                role="option"
                aria-selected={selected}
                disabled={disabled}
                onClick={() => onChange(voice.id)}
                className={cx(
                  "flex min-w-0 flex-1 flex-col items-start gap-0.5 rounded text-left",
                  "disabled:cursor-not-allowed",
                )}
              >
                <span className="flex items-center gap-2">
                  <span
                    aria-hidden="true"
                    className={cx(
                      "h-1.5 w-1.5 rounded-full",
                      selected ? "bg-bright" : "bg-line",
                    )}
                  />
                  <span className={cx("text-sm", selected ? "text-bright" : "text-body")}>
                    {voice.name}
                  </span>
                  <span className="truncate font-mono text-xs text-muted">{voice.id}</span>
                </span>
                <span className="pl-3.5 text-xs text-muted">{voice.style}</span>
              </button>
              <PreviewButton provider={provider} disabled={disabled} />
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Realtime picker
// ---------------------------------------------------------------------------

export interface RealtimeVoicePickerProps {
  value: string;
  onChange: (voice: string) => void;
  error?: string | null;
  disabled?: boolean;
}

export function RealtimeVoicePicker({
  value,
  onChange,
  error,
  disabled = false,
}: RealtimeVoicePickerProps) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium text-body">Voice</span>
        <span className="text-xs text-muted">Fixed OpenAI set — no voice cloning</span>
      </div>
      <div role="radiogroup" aria-label="Realtime voice" className="flex flex-wrap gap-2">
        {REALTIME_VOICES.map((voice) => {
          const selected = voice === value;
          return (
            <button
              key={voice}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={disabled}
              onClick={() => onChange(voice)}
              className={cx(
                "rounded border px-2.5 py-1 font-mono text-xs capitalize transition-colors",
                selected
                  ? "border-bright bg-surface-raised text-bright"
                  : "border-line text-body hover:border-muted",
                "disabled:cursor-not-allowed disabled:opacity-50",
              )}
            >
              {voice}
            </button>
          );
        })}
      </div>
      {error && (
        <p className="text-xs text-danger" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Gemini picker
// ---------------------------------------------------------------------------

export interface GeminiVoicePickerProps {
  value: string;
  onChange: (voice: string) => void;
  error?: string | null;
  disabled?: boolean;
}

export function GeminiVoicePicker({
  value,
  onChange,
  error,
  disabled = false,
}: GeminiVoicePickerProps) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium text-body">Voice</span>
        <span className="text-xs text-muted">Fixed Google set — no voice cloning</span>
      </div>
      <div role="radiogroup" aria-label="Gemini voice" className="flex flex-wrap gap-2">
        {GEMINI_VOICES.map((voice) => {
          const selected = voice === value;
          return (
            <button
              key={voice}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={disabled}
              onClick={() => onChange(voice)}
              className={cx(
                "rounded border px-2.5 py-1 font-mono text-xs transition-colors",
                selected
                  ? "border-bright bg-surface-raised text-bright"
                  : "border-line text-body hover:border-muted",
                "disabled:cursor-not-allowed disabled:opacity-50",
              )}
            >
              {voice}
            </button>
          );
        })}
      </div>
      {error && (
        <p className="text-xs text-danger" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
